import { defineStore } from "pinia";
import { _GettersTree, _ActionsTree, StoreDefinition } from "pinia";
import type { IConfigurationApp, ISettings } from "@/@types/configuration-app/app-config";
import type {
  HotelSettingsBase,
  HotelType,
  ShipSettings,
} from "@/@types/hotel-services/settings";
import type { LanguageCode, Locale } from "@/@types/shared/locale";
import type { RoutingPage } from "@/@types/shared/nav/nav-bar";

interface IConfig {
  app: IConfigurationApp;
  settings: ISettings;
  hotel: HotelSettingsBase | null;
  customer: string;
  routes: RoutingPage[];
}

const getCustomer = (): string =>
  (process.env.VUE_APP_BRAND || "customer-don").split("-").pop() || "ksk";

const state = (): IConfig => ({
  app: {
    background: "",
    logo: "",
  },
  settings: {
    languages: ["ru", "en", "zh"],
    weather: "",
  },
  hotel: null,
  customer: getCustomer(),
  routes: [],
});

const isShip = (hotel: HotelSettingsBase | null): hotel is ShipSettings =>
  hotel?.type === "ship";

const getters: _GettersTree<IConfig> = {
  getBackground: (state: IConfig): string => state.app.background,
  getLogo: (state: IConfig): string => state.app.logo,
  getLags: (state: IConfig): Array<LanguageCode> => state.settings.languages,
  getMMSI: (state: IConfig): string =>
    isShip(state.hotel) ? state.hotel.params.mmsi : "",
  getHotelType: (state: IConfig): HotelType | null => state.hotel?.type ?? null,
  getHotelName: (state: IConfig): Locale => state.hotel?.name ?? {},
  getWeather: (state: IConfig): string => state.settings.weather,
  getWifi: (state: IConfig): { name: string; password: string } =>
    state.hotel?.wifi_config ?? { name: "", password: "" },
  getCustomer: (state: IConfig): string => state.customer,
  getRoutes: (state: IConfig): Array<RoutingPage> => state.routes,
};

export const configurations: StoreDefinition<
  string,
  IConfig,
  _GettersTree<IConfig>,
  _ActionsTree
> = defineStore<string, IConfig>("configurations", {
  state,
  getters,
  actions: {
    setBackground(background: string) {
      this.app.background = background;
    },
    setLogo(logo: string) {
      this.app.logo = logo;
    },
    setLangs(langs: Array<LanguageCode>) {
      this.settings.languages = langs;
    },
    setWeather(payload: string) {
      this.settings.weather = payload;
    },
    setHotel(payload: HotelSettingsBase) {
      this.hotel = payload;
    },
    setWifi(payload: { name: string; password: string }) {
      if (!this.hotel) return;
      this.hotel.wifi_config = payload;
    },
    setRoutes(payload: Array<RoutingPage>) {
      this.routes = payload;
    },
  },
});
